import _ from 'lodash'
import { set } from 'lodash'
import { from, Observable, of, throwError, zip } from 'rxjs'
import { catchError, concatMap, first, last, switchMap } from 'rxjs/operators'

import {
    createTranslatedString, 
    ConditionReport,
    DefectAlteration, 
    DefectMaterial, 
    DefectObservation,
    PredictInterventionResource, 
    StateOfConservationResource,
    Signer,
    Wemi,
    SynchroMediaUpdates,
    ConditionReportState,
    ConditionReportValidationAction,
    ConditionReportStatePrefix,
} from '@skinsoft/types'
import { SEARCH_TYPES } from '@skinsoft/const'
import { involvedItem, referenceNumber } from '@skinsoft/selectors/condition-report-selectors'
import { ConditionReportTransitionStateError } from './errors'
import { DATE_FORMATS, formatCurrentDate, getCurrentDateAsSkinDate } from '@skinsoft/utils'
import { API_ENDPOINTS } from '@skinsoft/network'
import { EntityDataStore } from '@skinsoft/entity-data-store'
import { ConditionReportPDFGenerator } from '@skinsoft/pdf-generator'
import { ConditionReportPDFTransferHelper, MediaTransferHelper } from '@skinsoft/media-transfer-helper'
import { WemiUtils } from '@skinsoft/wemi-utils'
import { CACHE_KEYS, ConditionReportOfflineDataFetcher } from '@skinsoft/offline'

interface AdditionalSynchroData {
    alterationsMedia?: { media?: string }[],
}

export const getMinimumObservations = (locale: string) => {
    return [
        {
            id: 0,
            typeOfObservation: '',
            // tslint:disable-next-line:object-literal-sort-keys
            materialAlterations: getMinimumMaterials(locale),
        },
    ] as DefectObservation[]
}

export const getMinimumMaterials = (locale: string) => {
    return [
        {
            id: 0,
            material: '',
            // tslint:disable-next-line:object-literal-sort-keys
            alterations: getMinimumAlterations(locale),
        },
    ] as DefectMaterial[]
}

export const getMinimumAlterations = (locale: string) => {
    return [
        {
            id: 0,
            typeOfAlteration: '',
            // tslint:disable-next-line:object-literal-sort-keys
            precision: createTranslatedString(locale),
            annotations: [],
        },
    ] as DefectAlteration[]
}

export class ConditionReportDataStore extends EntityDataStore<ConditionReport> {
    public static getInstance() {
        if (!ConditionReportDataStore.instance) {
            ConditionReportDataStore.instance = new ConditionReportDataStore()
        }

        return ConditionReportDataStore.instance
    }

    public static install(Vue) {
        Vue.prototype.$conditionReportDataStore = ConditionReportDataStore.getInstance()
    }

    private static instance: ConditionReportDataStore = null as any

    constructor() {
        super('conditionReports', [SEARCH_TYPES.conditionReport])
    }

    public exists(id: ConditionReport['id']) {
        return this.fetchOne(id)
            .pipe(first())
            .toPromise()
    }

    public fetchOneOnlineMode(id: string): Observable<ConditionReport> {
        return super.fetchOneOnlineMode(id).pipe(
            switchMap(conditionReport => {
                if(!conditionReport.associatedMedias) {
                    _.set(conditionReport, 'associatedMedias', {
                        medias: [],
                        first: undefined,
                        second: undefined,
                        third: undefined
                    })
                }

                return of(conditionReport)
            })
        )
    } 

    public addPDFToReport(reportId: string, pdfId: string) {
        return this.fetchOneOnlineMode(reportId).pipe(
            switchMap(report => {
                let reportUpdates: Partial<ConditionReport> = {}

                let associatedMedias = report.associatedMedias
                associatedMedias.medias = associatedMedias.medias ? 
                    associatedMedias.medias.concat([pdfId]) 
                    : [pdfId]

                associatedMedias.medias = _.uniq(associatedMedias.medias)
                reportUpdates.associatedMedias = MediaTransferHelper.permutateFirstMedia(
                    pdfId, 
                    associatedMedias
                )
                const body = this.mergeOverridingArrays(report, reportUpdates)

                return this.update(reportId, body)
            })
        )
    }

    public async closeConditionReport(reportId: string, mediaId: string): Promise<ConditionReport> {
        const hasCachedUpdates = await this.hasCachedUpdates(reportId)
        if(window.navigator.onLine && ! hasCachedUpdates && ! MediaTransferHelper.isLocalMedia(mediaId)) {
            return this.addPDFToReport(reportId, mediaId).pipe(
                concatMap(() => {
                    return this.transitionReportState(
                        reportId, 
                        ConditionReportState.CLOSED, 
                        ConditionReportValidationAction.UPDATE_ALL
                    )
                }),
                concatMap(() => this.fetchOne(reportId, true)),
                first(),
            )
            .toPromise()
        }
        else {
            const reportUpdates: Partial<ConditionReport> = {
                workflowSchema: {
                    currentState: {
                        name: 'validated'
                    }
                }
            }

            return this.fetchOneOfflineMode(reportId).pipe(
                switchMap(report => {
                    reportUpdates.associatedMedias = MediaTransferHelper.permutateFirstMedia(mediaId, report.associatedMedias)
                    return this.updateOfflineMode(reportId, reportUpdates)
                })
            )
            .toPromise()
        }
    }

    public async closeConditionReportOffline(reportId: string) {
        let reportUpdates: Partial<ConditionReport> = {
            workflowSchema: {
                currentState: {
                    name: 'validated'
                }
            }
        }

        return this.fetchOneOfflineMode(reportId).pipe(
            switchMap(report => {
                return this.updateOfflineMode(reportId, reportUpdates)
            })
        )
        .toPromise()
    }

    public generateAndUploadPDF(conditionReport: ConditionReport, filterPdfs: boolean = true, locale?: string): Observable<string> {
        const itemId = involvedItem(conditionReport)

        return WemiUtils.getWemiFromItem(itemId).pipe(
            switchMap((associatedItem: Wemi) => {
                // We use a promise here in order to wait for the PDFGenerator.generatePDF to complete (using the callback argument) 
                return from(new Promise<File>((resolve, reject) => {
                    if(associatedItem) {
                        ConditionReportPDFGenerator.getInstance().generatePDF(
                            conditionReport, 
                            associatedItem, 
                            (blob) => {
                                const datePrefix = formatCurrentDate(DATE_FORMATS.DD_MM_YYYY_DASH_HMS_ONE_BLOCK)
                                const inventoryNumber = referenceNumber(conditionReport)
                                const fileName = datePrefix ? 
                                    `${inventoryNumber}-${datePrefix}.pdf` 
                                    : `${datePrefix}.pdf`
                                resolve(new File([blob], fileName))
                            },
                            locale,
                            undefined,
                            filterPdfs
                        )
                        .catch(err => {
                            reject()
                        })

                    } else {
                        reject()
                    }
                }))
            }),
            switchMap(file => ConditionReportPDFTransferHelper.getInstance().uploadPDFDocument("application/pdf", conditionReport.id, file, true)),
        )
    }

    private removeLocalMediaFromAlterations(dataToPush: SynchroMediaUpdates<ConditionReport>, additionalSynchroData: AdditionalSynchroData) {
        additionalSynchroData.alterationsMedia = []
        if(dataToPush.body.conditionReportCS?.alterations && Array.isArray(dataToPush.body.conditionReportCS.alterations)) {
            for(let alteration of dataToPush.body.conditionReportCS.alterations) {
                if(alteration.media?.startsWith('local')){
                    additionalSynchroData.alterationsMedia.push({ media: alteration.media })
                    alteration.media = null
                    continue
                }

                additionalSynchroData.alterationsMedia.push({ })
            }
        }
    }

    public addMedia(reportId: string, media: {newMediaId: string}, throwErrorOnFailure: boolean = false) {
        return this.proxy.fetch(
            `/conditionReports/addMedia/${reportId}`,
            {idMedia: media.newMediaId}, 
            'PUT',
            undefined,
            undefined,
            !throwErrorOnFailure,
        )
    }

    public duplicate(entityToDuplicateId: string, createBody?: Partial<ConditionReport>, body?: Partial<ConditionReport>): Observable<ConditionReport> {
        return this.fetchOneOnlineMode(entityToDuplicateId).pipe(
            switchMap(entityToDuplicate => {
                return zip(
                    of(entityToDuplicate),
                    super.duplicate(entityToDuplicateId) 
                )
            }),
            switchMap(([originalReport, duplicatedReport]) => {
                const fixedReport = _.cloneDeep(duplicatedReport)
                
                delete (fixedReport as any).conditionReportCS.referenceNumber
                _.set(fixedReport, "conditionReportCS.reportDate", getCurrentDateAsSkinDate())
                
                if(body?.associatedMedias) {
                    _.set(fixedReport, "associatedMedias", body.associatedMedias)
                }
                else if(originalReport.associatedMedias) {
                    _.set(fixedReport, "associatedMedias", originalReport.associatedMedias)
                }
                
                const signersWithoutSignature: Signer[] = _.get(
                    fixedReport, 
                    "conditionReportCS.signers", 
                    []
                ).map(signer => {
                    signer.signature = ''
                    return signer
                })
                _.set(fixedReport, "conditionReportCS.signers", signersWithoutSignature)
        
                _.set(fixedReport, "workflowSchema.currentState.name", ConditionReportState.IN_PROGRESS)  
                
                return this.update(fixedReport.id, fixedReport)
            }),
        )
    }

    public create(
        createBody?: Partial<ConditionReport>,
        body?: Partial<ConditionReport>,
    ): Observable<ConditionReport> {
        if (window.navigator.onLine) {
            return super.create(createBody, body)
        } else {
            if (!body) {
                return this.createOffline()
            } else {
                return super.create(createBody, body)
            }
        }
    }

    public createOffline(): Observable<ConditionReport> {
        const tempId =
            '_' +
            Math.random()
                .toString(36)
                .substr(2, 9) +
            Math.random()
                .toString(36)
                .substr(2, 9)
        const emptyReport = {
            id: tempId,
            conditionReportCS: {
                signers: [] as Signer[],
                stateOfConservations: [] as StateOfConservationResource[],
                predictInterventions: [] as PredictInterventionResource[],
                alterationDetails: null
            },
            workflowSchema: {
                currentState: {
                    name: 'inProgress',
                    availableLifecycleStates: ['validated']
                }
            },
        }

        return from(this.offlineDataUpdateCacher.storeBeforePushing(
            tempId, 
            emptyReport as Partial<ConditionReport> & { id: string }
        )).pipe(
            switchMap(conditionReport => {
                if (!conditionReport) {
                    throw new Error('ConditionReport not yet available')
                }
                return this.fetchOneOfflineMode(tempId)
            }),
        )
    }

    public update(
        id: string,
        body: Partial<ConditionReport>,
        endpointOverride?: string,
        ignoreCachedStatus: boolean = false,
    ): Observable<ConditionReport> {
        return super.update(
            id, 
            body, 
            endpointOverride || API_ENDPOINTS['conditionReport'], 
            ignoreCachedStatus
        )
    }

    public updateOfflineMode(
        id: string,
        body: Partial<ConditionReport>,
    ): Observable<ConditionReport> {
        return super.updateOfflineMode(id, body)
    }

    public async setInvolvedItemAndSave(reportId: string, itemId: string) {
        const report: ConditionReport = await this.fetchOne(reportId)
            .pipe(first())
            .toPromise()

        set(report, 'conditionReportCS.linkableItem.linkableItem', itemId)
    }

    protected initializeDataFetcher() {
        this.offlineDataFetcher = new ConditionReportOfflineDataFetcher(CACHE_KEYS.ConditionReport);
    }

    public transitionReportState(reportId: string, state: ConditionReportState, conditionReportValidationAction: ConditionReportValidationAction) {
        const urlSearchParams = new URLSearchParams({
            id: reportId,
            lifecycleState: state.replace(ConditionReportStatePrefix, ''),
            conditionReportValidationAction: conditionReportValidationAction,
        })
        const uri = `${this.endpoint}?${urlSearchParams}`

        return this.proxy.fetch(
            uri,
            undefined,
            "PATCH"
        ).pipe(
            last(),
            //tap(data => this.offlineDataFetcher.updateOne(data.id, data)),
            catchError(error => {
                return throwError(new ConditionReportTransitionStateError())
            }),
        )
    }
}
